import { Box, Chip, Typography } from '@mui/material'
import { useSetAtom } from 'jotai'
import type { Recipe } from '@/types'
import {
  CRAFT_TYPE_LABEL,
  getCrystalColor,
  shouldUseDarkCrystalText,
  SKILL_TIERS,
} from '@/types/constants'
import {
  itemSearchQueryAtom,
  mainTabAtom,
  selectedCraftAtom,
  selectedCraftingInnerTabAtom,
  selectedTierAtom,
} from '@/atoms'
import { isDesynthesisRecipe } from '@/data'

interface Props {
  recipe: Recipe
  highlightQuery?: string
  hideIngredients?: boolean
  enableSkillNavigation?: boolean
}

function highlight(text: string, query?: string) {
  if (!query || !text.includes(query)) return <>{text}</>
  const idx = text.indexOf(query)
  return (
    <>
      {text.slice(0, idx)}
      <strong style={{ backgroundColor: '#fff176' }}>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  )
}

const clickableSx = {
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline', color: 'primary.main' },
} as const

export function RecipeCard({
  recipe,
  highlightQuery,
  hideIngredients = false,
  enableSkillNavigation = false,
}: Props) {
  const setSearch = useSetAtom(itemSearchQueryAtom)
  const setMainTab = useSetAtom(mainTabAtom)
  const setSelectedCraft = useSetAtom(selectedCraftAtom)
  const setSelectedCraftingInnerTab = useSetAtom(selectedCraftingInnerTabAtom)
  const setSelectedTier = useSetAtom(selectedTierAtom)
  const { result, skills, crystal, ingredients, hqResults } = recipe
  const isDesynthesis = isDesynthesisRecipe(recipe)
  const primarySkill = skills[0]
  const subSkills = skills.slice(1)

  function handleSkillClick(skill: (typeof skills)[number]) {
    if (!enableSkillNavigation) return

    const tier = SKILL_TIERS.find((t) => skill.level >= t.min && skill.level <= t.max)
    const nextMode = isDesynthesis ? 'desynthesis' : 'synthesis'
    setMainTab('crafting')
    setSelectedCraftingInnerTab(nextMode)
    setSelectedCraft(skill.craft)
    setSelectedTier(tier?.index ?? null)
  }

  const skillSx = enableSkillNavigation ? clickableSx : undefined

  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: hideIngredients
            ? '76px minmax(150px, 1fr) 36px'
            : '76px 176px 36px 1fr',
          gap: 1,
          alignItems: 'start',
        }}
      >
        {/* スキルレベル */}
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            <Typography
              component="span"
              variant="caption"
              onClick={() => handleSkillClick(primarySkill)}
              sx={skillSx}
            >
              {CRAFT_TYPE_LABEL[primarySkill.craft]}
              {primarySkill.level}
            </Typography>
          </Typography>
          {subSkills.map((s, i) => (
            <Typography key={i} variant="caption" color="text.disabled" display="block" noWrap>
              <Typography
                component="span"
                variant="caption"
                onClick={() => handleSkillClick(s)}
                sx={skillSx}
              >
                {CRAFT_TYPE_LABEL[s.craft]}
                {s.level}
              </Typography>
            </Typography>
          ))}
        </Box>

        {/* 完成品 */}
        <Box sx={{ overflow: 'hidden' }}>
          <Typography
            variant="body2"
            fontWeight={500}
            noWrap
            onClick={() => setSearch(result.name)}
            sx={clickableSx}
          >
            {highlight(result.name, highlightQuery)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ×{result.quantity}
            </Typography>
          </Typography>
          {hqResults.length > 0 && (
            <Typography variant="caption" color="primary.main" display="block" noWrap>
              HQ: {hqResults.map((hq) => `${hq.name}×${hq.quantity}`).join(' / ')}
            </Typography>
          )}
        </Box>

        {/* 属性 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={isDesynthesis ? '分解' : '合成'}
            size="small"
            sx={{
              bgcolor: isDesynthesis ? '#ff9800' : '#29b6f6',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 20,
              width: 32,
              '& .MuiChip-label': { px: 0.25 },
            }}
          />
          <Chip
            label={crystal}
            size="small"
            sx={{
              bgcolor: getCrystalColor(crystal),
              color: shouldUseDarkCrystalText(crystal) ? 'text.primary' : 'white',
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 20,
              width: 32,
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        </Box>

        {/* 素材 */}
        {!hideIngredients && (
          <Box>
            {ingredients.map((ing, i) => (
              <Typography
                key={i}
                component="span"
                variant="caption"
                color="text.secondary"
                onClick={() => setSearch(ing.name)}
                sx={clickableSx}
              >
                {highlight(ing.name, highlightQuery)}×{ing.quantity}
                {i < ingredients.length - 1 && '、'}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}
